import { useMemo, useState } from 'react';
import { Box, Typography, Skeleton, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEventMarkets } from '../../api/hooks';
import { openInNewTab } from '../../../../utils/newTab';
import { useMultiMarketProbability } from '../../api/useMultiMarketProbability';
import { ConvergenceChart, type MarketLineMeta } from './convergence';
import { assignMarketColors } from '../../utils/marketColors';
import { useEventSignals } from '../../api/useSignals';
import { useEventAlerts } from '../../api/useAlerts';
import {
    AlertsOverlayData,
    SignalItem,
    SignalOverlayData,
} from '../../api/types';

interface EmbeddedEventChartProps {
    eventId: string;
    slug?: string;
    /** Chart area height in px, not including the header strip. */
    height?: number;
    /**
     * When provided, only these condition IDs are rendered in the chart.
     * The backend uses this to focus the view on the markets relevant to
     * the query rather than showing all markets in the event.
     */
    conditionIdFilter?: string[];
    /**
     * ISO date strings [from, to] for the initial chart viewport.
     * The user can still pan/zoom after this is applied.
     */
    timeRange?: [string, string];
    /**
     * Subset of MARKET_SIGNAL_TYPES to overlay. When omitted the chart
     * renders clean with no signal markers, which is the right default for
     * embedded contexts. When provided, only signals matching these types
     * (and conditionIdFilter if set) are shown.
     */
    signalTypeFilter?: string[];
    /** When true, alert markers are overlaid on the chart. */
    showAlerts?: boolean;
}

function parseTimeRange(
    raw: [string, string] | undefined
): [Date, Date] | null {
    if (!raw) return null;
    const from = new Date(raw[0]);
    const to = new Date(raw[1]);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from >= to) return null;
    return [from, to];
}

export function EmbeddedEventChart({
    eventId,
    slug,
    height = 300,
    conditionIdFilter,
    timeRange,
    signalTypeFilter,
    showAlerts = false,
}: EmbeddedEventChartProps) {
    const theme = useTheme();

    // Controlled time range — initialised from the prop so the chart starts at
    // the requested viewport, but user pan/zoom is reflected back via
    // onTimeRangeChange instead of being silently dropped by a noop handler.
    const [activeTimeRange, setActiveTimeRange] = useState<[Date, Date] | null>(
        () => parseTimeRange(timeRange)
    );

    const {
        data: eventData,
        loading,
        error,
    } = useEventMarkets(slug ?? eventId);

    const allMarkets = eventData?.markets ?? [];

    const markets = useMemo(() => {
        if (!conditionIdFilter || conditionIdFilter.length === 0)
            return allMarkets;
        const ids = new Set(conditionIdFilter);
        return allMarkets.filter(m => ids.has(m.condition_id));
    }, [allMarkets, conditionIdFilter]);

    const isMultiMarket = markets.length > 1;

    const colorMap = useMemo(() => {
        const sorted = [...markets].sort(
            (a, b) => (b.current_yes_prob ?? 0) - (a.current_yes_prob ?? 0)
        );
        return assignMarketColors(sorted.map(m => m.condition_id));
    }, [markets]);

    const conditionIds = useMemo(
        () => markets.map(m => m.condition_id),
        [markets]
    );

    const startDate = useMemo(
        () => (eventData?.start_date ? new Date(eventData.start_date) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [eventData?.start_date]
    );

    const endDate = useMemo(
        () => (eventData?.end_date ? new Date(eventData.end_date) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [eventData?.end_date]
    );

    const { series: probSeries, loading: probLoading } =
        useMultiMarketProbability(conditionIds, startDate, endDate, 720);

    const marketMeta: MarketLineMeta[] = useMemo(
        () =>
            [...markets]
                .sort(
                    (a, b) =>
                        (b.current_yes_prob ?? 0) - (a.current_yes_prob ?? 0)
                )
                .map(m => ({
                    conditionId: m.condition_id,
                    question:
                        isMultiMarket && m.yes_outcome
                            ? m.yes_outcome
                            : m.question,
                    color:
                        colorMap.get(m.condition_id) ??
                        theme.palette.primary.main,
                })),
        [markets, colorMap, theme, isMultiMarket]
    );

    // Only fetch signals when the backend explicitly requests a signal overlay.
    // Passing null suppresses the fetch — useEventSignals already guards on null.
    const { signals: allSignals } = useEventSignals(
        signalTypeFilter ? eventData?.event_id ?? null : null,
        500
    );

    // Same pattern for alerts — only fetch when the overlay was requested.
    const { alerts } = useEventAlerts(
        showAlerts ? eventData?.event_id ?? null : null
    );

    const signalOverlay = useMemo((): SignalOverlayData | undefined => {
        if (!signalTypeFilter) return undefined;

        const allowedTypes = new Set(signalTypeFilter);
        const allowedMarkets =
            conditionIdFilter && conditionIdFilter.length > 0
                ? new Set(conditionIdFilter)
                : null;

        const filtered = allSignals.filter((sig: SignalItem) => {
            const typeMatch =
                allowedTypes.size === 0 || allowedTypes.has(sig.signal_type);
            const marketMatch =
                allowedMarkets === null || allowedMarkets.has(sig.market_id);
            return typeMatch && marketMatch;
        });

        const byMarket = new Map<string, SignalItem[]>();
        for (const sig of filtered) {
            const bucket = byMarket.get(sig.market_id) ?? [];
            bucket.push(sig);
            byMarket.set(sig.market_id, bucket);
        }

        return { byMarket, colorMap };
    }, [signalTypeFilter, allSignals, conditionIdFilter, colorMap]);

    const alertsOverlay = useMemo(
        (): AlertsOverlayData | undefined =>
            showAlerts ? { alerts } : undefined,
        [showAlerts, alerts]
    );

    const href = `/markets/event/${slug ?? eventId}`;

    if (loading) {
        return (
            <Skeleton
                variant="rounded"
                height={height + 36}
                sx={{ borderRadius: '6px' }}
            />
        );
    }

    if (error || !eventData) {
        return (
            <Box
                sx={{
                    height: height + 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '6px',
                }}
            >
                <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>
                    Failed to load event
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '6px',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.875,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: '12px',
                        fontWeight: 600,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {eventData.event_title}
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => openInNewTab(href)}
                    sx={{ color: 'text.disabled', flexShrink: 0 }}
                    title="Open full view"
                >
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Box>

            {/* Chart */}
            <Box sx={{ height }}>
                {markets.length > 0 && (
                    <ConvergenceChart
                        series={probSeries}
                        marketMeta={marketMeta}
                        loading={probLoading}
                        highlightedMarkets={null}
                        onMarketClick={() => {}}
                        resolution={720}
                        onResolutionChange={() => {}}
                        timeRange={activeTimeRange}
                        onTimeRangeChange={setActiveTimeRange}
                        eventStartDate={startDate}
                        eventEndDate={endDate}
                        signalOverlay={signalOverlay}
                        alertsOverlay={alertsOverlay}
                        showVolume={true}
                    />
                )}
            </Box>
        </Box>
    );
}
